import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import useSWR from 'swr'
import { useDebounce } from '../hooks'
import { api, updateTournamentSettings } from '../services/api'
import { Button, CompetitionLevelLogosModal, Pagination } from '../components'
import pageStyles from '../styles/page.module.scss'
import listTableStyles from '../styles/list-table.module.scss'
import buttonStyles from '../components/Button.module.scss'
import styles from './CompetitionsList.module.scss'

interface Competition {
  tc_competition_parent_id: string
  ext_competition_parent_id: string
  tc_name: string
  ext_name: string
  tc_official_name: string | null
  ext_official_name: string | null
  display_name: string
  secondary_display_name: string | null
  is_hidden: boolean
  sport_name: string
  catalog_source_name: string
  created_at: string
  updated_at: string
  nearest_season_start: string | null
  nearest_season_end: string | null
}

interface CompetitionsResponse {
  competitions: Competition[]
  pagination: {
    page: number
    limit: number
    total_count: number
    total_pages: number
    has_next: boolean
    has_prev: boolean
  }
  filters: {
    name: string
  }
  timestamp: string
}

const CompetitionsList: React.FC = () => {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [isLevelLogosOpen, setIsLevelLogosOpen] = useState(false)
  const [pendingHidden, setPendingHidden] = useState<Record<string, boolean>>({})
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set())
  const [actionError, setActionError] = useState<string | null>(null)
  const pageSize = 100

  const debouncedSearchTerm = useDebounce(searchTerm, 300)

  // Fetch competitions
  const { data, isLoading, error, mutate } = useSWR(
    ['competitions', { page: currentPage, limit: pageSize, name: debouncedSearchTerm }],
    () => api.get('/competitions', { page: currentPage, limit: pageSize, name: debouncedSearchTerm?.trim() || undefined }),
    { revalidateOnFocus: false }
  )

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearchTerm])

  const clearSearch = () => {
    setSearchTerm('')
  }

  const handleToggleHidden = async (parentId: string, next: boolean) => {
    setActionError(null)
    setPendingHidden((prev) => ({ ...prev, [parentId]: next }))
    setTogglingIds((prev) => new Set(prev).add(parentId))
    try {
      await updateTournamentSettings(parentId, { is_hidden: next })
      await mutate()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      setActionError(`Failed to update visibility: ${message}`)
    } finally {
      setPendingHidden((prev) => {
        const copy = { ...prev }
        delete copy[parentId]
        return copy
      })
      setTogglingIds((prev) => {
        const copy = new Set(prev)
        copy.delete(parentId)
        return copy
      })
    }
  }

  if (error) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.error}>
          <h2>Error loading competitions</h2>
          <p>Failed to fetch competitions: {(error as Error).message}</p>
          <Button variant="danger" onClick={() => mutate()}>Retry</Button>
        </div>
      </div>
    )
  }

  const competitionsData: CompetitionsResponse = data?.body ? JSON.parse(data.body) : data
  const competitions = competitionsData?.competitions || []
  const pagination = competitionsData?.pagination

  const getExternalId = (competition: Competition) => {
    return competition.ext_competition_parent_id || 'N/A'
  }

  const formatSeasonRange = (startDate: string | null, endDate: string | null) => {
    if (!startDate) return '—'
    try {
      const start = new Date(startDate)
      const startFormatted = start.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })

      if (!endDate) {
        return `${startFormatted}, ${start.getFullYear()}`
      }

      const end = new Date(endDate)
      const sameYear = start.getFullYear() === end.getFullYear()

      const endFormatted = end.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })

      if (sameYear) {
        return `${startFormatted} – ${endFormatted}, ${end.getFullYear()}`
      } else {
        return `${startFormatted}, ${start.getFullYear()} – ${endFormatted}, ${end.getFullYear()}`
      }
    } catch {
      return '—'
    }
  }

  return (
    <div className={pageStyles.pageContainer}>
      {/* Header Buttons */}
      <div className={pageStyles.headerButtons}>
        <Button onClick={() => navigate('/tournaments/new')}>
          + Add Tournament
        </Button>
      </div>

      <div className={pageStyles.pageHeader}>
        <h1>Tournaments</h1>
      </div>

      <div className={styles.secondaryActions}>
        <Button variant="secondary" onClick={() => setIsLevelLogosOpen(true)}>
          <span className={styles.buttonContent}>
            <svg
              className={styles.buttonIcon}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <circle cx="9" cy="10" r="1.5" />
              <path d="m21 16-5.5-5.5L8 18" />
              <path d="m13.5 13.5 1.5-1.5 3 3" />
            </svg>
            <span>Tournament Level Logos</span>
          </span>
        </Button>
      </div>

      {/* Search and Filters */}
      <div className={listTableStyles.searchSection}>
        <div className={listTableStyles.searchForm}>
          <div className={listTableStyles.searchInputGroup}>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search tournaments by name..."
              className={listTableStyles.searchInput}
            />
            {searchTerm && (
              <button type="button" onClick={clearSearch} className={listTableStyles.clearButton}>
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {actionError && (
        <div className={pageStyles.error}>
          <p>{actionError}</p>
        </div>
      )}

      {isLoading && !data ? (
        <div className={pageStyles.loading}>
          <h3>Loading tournaments...</h3>
          <p>Please wait while we fetch the tournament data.</p>
        </div>
      ) : competitions.length === 0 ? (
        <div className={listTableStyles.noResults}>
          <h3>No tournaments found</h3>
          <p>
            {debouncedSearchTerm
              ? `No tournaments match "${debouncedSearchTerm}". Try a different search term.`
              : 'Unable to load tournament data.'
            }
          </p>
        </div>
      ) : (
        <>
          <div className={`${listTableStyles.tableHeader} ${styles.listHeader}`}>
            <div>Name</div>
            <div>External ID</div>
            <div>Next Season</div>
            <div className={styles.hiddenCell}>
              <span className={styles.hiddenHeading}>
                Hidden
                {/* TODO: remove the "(tennis.com)" subtext once Hidden applies to all applications */}
                <span className={styles.hiddenSubtext}>(tennis.com)</span>
              </span>
            </div>
            <div></div>
          </div>

          <div className={listTableStyles.tableBody}>
            {competitions.map((competition) => (
              <div key={competition.tc_competition_parent_id} className={`${listTableStyles.tableRow} ${styles.listItem}`}>
                <div className={listTableStyles.cellPrimary}>
                  <p className={listTableStyles.primaryText}>
                    {competition.display_name}
                  </p>
                  {competition.secondary_display_name && (
                    <p className={listTableStyles.secondaryText}>
                      {competition.secondary_display_name}
                    </p>
                  )}
                </div>

                <div className={listTableStyles.cellId}>
                  {getExternalId(competition)}
                </div>

                <div className={listTableStyles.cellText}>
                  {formatSeasonRange(competition.nearest_season_start, competition.nearest_season_end)}
                </div>

                <div className={styles.hiddenCell}>
                  <input
                    type="checkbox"
                    checked={pendingHidden[competition.tc_competition_parent_id] ?? competition.is_hidden}
                    onChange={(e) => handleToggleHidden(competition.tc_competition_parent_id, e.target.checked)}
                    disabled={togglingIds.has(competition.tc_competition_parent_id)}
                    title={(pendingHidden[competition.tc_competition_parent_id] ?? competition.is_hidden)
                      ? 'Hidden from Tennis Channel views - click to show'
                      : 'Visible in Tennis Channel views - click to hide'}
                  />
                </div>

                <div className={`${listTableStyles.cellActions} ${styles.actionsCell}`}>
                  <Link
                    to={`/tournaments/${competition.tc_competition_parent_id}`}
                    className={`${buttonStyles.button} ${buttonStyles.primary} ${buttonStyles.medium}`}
                  >
                    View
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {pagination && (
            <Pagination
              pagination={pagination}
              onPageChange={setCurrentPage}
              itemName="competitions"
            />
          )}
        </>
      )}

      <CompetitionLevelLogosModal
        isOpen={isLevelLogosOpen}
        onClose={() => setIsLevelLogosOpen(false)}
      />
    </div>
  )
}

export default CompetitionsList
